import React, { useState, useEffect, useRef } from "react";

/**
 * Modal image picker.
 *
 * Nov 1: the photos come first. Tags are one row — the most-used few — with
 * "More tags" to open the rest in a short scrolling box; the search box also
 * matches tags. A client with dozens of tags used to fill the modal with
 * chips and leave no room for the photos.
 * Props:
 *   clientId         — fetch images for this client
 *   onSelect(item)   — called for each selected media object (full _media_dict shape)
 *   onClose          — dismiss
 *   multi            — allow multi-select (default false)
 *   excludeIds       — media IDs already placed (shown dimmed)
 */
export default function ImagePicker({ clientId, onSelect, onClose, multi = false, excludeIds = [] }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const overlayRef = useRef();

  const [allTagsOpen, setAllTagsOpen] = useState(false);
  const tagCounts = images.reduce((m, img) => {
    (img.tags || []).forEach(t => m.set(t, (m.get(t) || 0) + 1));
    return m;
  }, new Map());
  const allTags = [...tagCounts.keys()].sort();
  const shownTags = topTags(tagCounts, TOP_TAGS, tagFilter);
  const moreCount = allTags.length - shownTags.length;

  useEffect(() => {
    fetch(`/api/clients/${clientId}/media`)
      .then(r => r.json())
      .then(data => { setImages(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [clientId]);

  // A contributor's photo can't be used until a coordinator approves it in
  // the Media Library (Oct 25) — so the picker doesn't offer it.
  const waiting  = images.filter(img => img.needs_approval).length;
  const filtered = images.filter(img => {
    if (img.needs_approval) return false;
    const q = search.trim().toLowerCase();
    if (q && !img.filename.toLowerCase().includes(q) &&
        !(img.alt_text || "").toLowerCase().includes(q) &&
        !(img.tags || []).some(t => t.toLowerCase().includes(q))) return false;
    if (tagFilter && !(img.tags || []).includes(tagFilter)) return false;
    return true;
  });

  function toggleSelect(id) {
    if (!multi) { setSelected(new Set([id])); return; }
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function confirm() {
    if (selected.size === 0) return;
    // Pass the full media object so callers can build a complete pending_image
    // without a second fetch (all fields needed by pe_adapter are already here).
    selected.forEach(id => {
      const item = images.find(img => img.id === id);
      if (item) onSelect(item);
    });
    onClose();
  }

  function onOverlayClick(e) {
    if (e.target === overlayRef.current) onClose();
  }

  return (
    <div style={styles.overlay} ref={overlayRef} onClick={onOverlayClick} role="dialog" aria-modal="true">
      <div style={styles.modal}>

        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>Choose an image</h2>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Filters */}
        <div style={styles.filterBar}>
          <input
            style={styles.searchInput}
            placeholder="Search by name, description or tag…"
            aria-label="Search images"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {allTags.length > 0 && (
            <div style={{ ...styles.tagChips, ...(allTagsOpen ? styles.tagChipsOpen : {}) }}>
              <button style={{ ...styles.tagChip, ...(tagFilter === null ? styles.tagChipActive : {}) }}
                onClick={() => setTagFilter(null)} aria-pressed={tagFilter === null}>All</button>
              {(allTagsOpen ? allTags : shownTags).map(tag => (
                <button key={tag} aria-pressed={tagFilter === tag}
                  style={{ ...styles.tagChip, ...(tagFilter === tag ? styles.tagChipActive : {}) }}
                  onClick={() => setTagFilter(tagFilter === tag ? null : tag)}>{tag}</button>
              ))}
              {(moreCount > 0 || allTagsOpen) && (
                <button style={styles.moreTags} onClick={() => setAllTagsOpen(o => !o)} aria-expanded={allTagsOpen}>
                  {allTagsOpen ? "Fewer tags ▴" : `More tags (${moreCount}) ▾`}
                </button>
              )}
            </div>
          )}
        </div>

        {waiting > 0 && (
          <p style={{ ...styles.empty, padding: "0 var(--space-4)", textAlign: "left" }}>
            {waiting === 1 ? "1 photo from a contributor isn't" : `${waiting} photos from contributors aren't`} shown
            — approve {waiting === 1 ? "it" : "them"} in the Media Library first.
          </p>
        )}

        {/* Grid — scrolls independently; minHeight:0 lets flex shrink it */}
        <div style={styles.grid}>
          {loading && <p style={styles.empty}>Loading…</p>}
          {!loading && filtered.length === 0 && <p style={styles.empty}>No images found.</p>}
          {filtered.map(img => {
            const isSelected = selected.has(img.id);
            const isExcluded = excludeIds.includes(img.id);
            return (
              <div
                key={img.id}
                style={{ ...styles.tile, ...(isSelected ? styles.tileSelected : {}), ...(isExcluded ? styles.tileExcluded : {}) }}
                onClick={() => !isExcluded && toggleSelect(img.id)}
                title={img.alt_text || img.filename}
              >
                {/*
                  KEY FIX: height:120 is set directly on the <img> element.
                  This gives the grid rows a concrete measurement to size against.
                  No wrapper div — removes the layer that was causing row-height mismatches.
                */}
                <div style={{ position: "relative" }}>
                  <img
                    src={img.thumb_url}
                    alt={img.alt_text || ""}
                    style={styles.thumbImg}
                  />
                  {isSelected && <div style={styles.check}>✓</div>}
                  {isExcluded && <div style={styles.excludedBadge}>Added</div>}
                </div>
                <p style={styles.tileName}>{img.filename}</p>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button style={styles.cancelBtn} onClick={onClose}>Cancel</button>
          <button
            style={{ ...styles.confirmBtn, ...(selected.size === 0 ? styles.confirmDisabled : {}) }}
            onClick={confirm}
            disabled={selected.size === 0}
          >
            {selected.size > 0
              ? `Add ${selected.size} image${selected.size > 1 ? "s" : ""}`
              : "Select an image"}
          </button>
        </div>
      </div>
    </div>
  );
}

const TOP_TAGS = 6;

/** The most-used few tags, ties by name — plus the chosen one if it isn't among them. */
export function topTags(counts, n, chosen = null) {
  const top = [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, n).map(([t]) => t);
  if (chosen && !top.includes(chosen)) top.push(chosen);
  return top;
}

const styles = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "var(--space-6)" },
  modal: { background: "var(--color-surface)", borderRadius: "var(--radius-xl)", width: "100%", maxWidth: 720, maxHeight: "85vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 24px 48px rgba(0,0,0,.25)" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "var(--space-5) var(--space-6)", borderBottom: "1px solid var(--color-border)", flexShrink: 0 },
  title: { fontSize: "var(--text-lg)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)" },
  closeBtn: { background: "none", border: "none", fontSize: "var(--text-lg)", color: "var(--color-text-tertiary)", cursor: "pointer" },
  filterBar: { display: "flex", flexDirection: "column", gap: "var(--space-3)", padding: "var(--space-4) var(--space-6)", borderBottom: "1px solid var(--color-border)", flexShrink: 0 },
  searchInput: { fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-3)", background: "var(--color-bg-base)", color: "var(--color-text-primary)", width: "100%" },
  tagChips: { display: "flex", gap: "var(--space-2)", flexWrap: "wrap", alignItems: "center" },
  // Opened, all tags sit in a short box of their own, so the photos keep the room.
  tagChipsOpen: { maxHeight: 96, overflowY: "auto", paddingRight: "var(--space-1)" },
  moreTags: { fontSize: "var(--text-xs)", padding: "2px var(--space-2)", border: "none", background: "none", color: "var(--color-accent)", cursor: "pointer", fontWeight: "var(--weight-medium)" },
  tagChip: { fontSize: "var(--text-xs)", padding: "2px var(--space-3)", borderRadius: "var(--radius-full)", border: "1px solid var(--color-border)", background: "var(--color-bg-base)", color: "var(--color-text-secondary)", cursor: "pointer" },
  tagChipActive: { background: "var(--color-accent)", color: "var(--color-text-on-accent)", borderColor: "var(--color-accent)" },

  // The grid itself scrolls. minHeight:0 is critical: without it, a flex child
  // defaults to minHeight:auto and refuses to shrink, pushing the footer off-screen
  // and preventing scroll from ever engaging.
  grid: { flex: 1, minHeight: 260, overflowY: "auto", gridAutoRows: "min-content", padding: "var(--space-5) var(--space-6)", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "var(--space-3)", alignContent: "start", alignItems: "start" },
  empty: { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", gridColumn: "1/-1", textAlign: "center", padding: "var(--space-10)" },

  // Tile: plain flex column. Height = image (120px) + filename text. Grid measures this correctly.
  tile: { display: "flex", flexDirection: "column", cursor: "pointer", borderRadius: "var(--radius-md)", border: "2px solid transparent", background: "var(--color-surface)", overflow: "hidden" },
  tileSelected: { border: "2px solid var(--color-accent)" },
  tileExcluded: { opacity: 0.5, cursor: "default" },

  // The img itself carries the fixed height. No wrapper between tile and img.
  // width:100% fills the grid column. height:120 is the explicit row height signal to the grid.
  thumbImg: { display: "block", maxWidth: "100%", maxHeight: 120, width: "auto", height: "auto", borderRadius: 6 },

  check: { position: "absolute", top: "var(--space-1)", right: "var(--space-1)", background: "var(--color-accent)", color: "#fff", borderRadius: "var(--radius-full)", width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: "bold" },
  excludedBadge: { position: "absolute", top: "var(--space-1)", right: "var(--space-1)", background: "var(--color-bg-subtle)", color: "var(--color-text-tertiary)", borderRadius: "var(--radius-sm)", fontSize: 10, padding: "1px var(--space-2)" },
  tileName: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", padding: "var(--space-2) var(--space-2) var(--space-2)" },

  footer: { display: "flex", justifyContent: "flex-end", gap: "var(--space-3)", padding: "var(--space-4) var(--space-6)", borderTop: "1px solid var(--color-border)", flexShrink: 0 },
  cancelBtn: { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  confirmBtn: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  confirmDisabled: { opacity: 0.5, cursor: "not-allowed" },
};
